import React, { useEffect, useMemo, useState } from 'react';
import { Box, Breadcrumbs, Link } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { PaginationFilterType } from '@types';
import { DynamicCard, Table } from '@components';
const money = (value) => (value ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const EMPTY = { formas: [], tecnicos: [], totais: { porForma: {}, total: 0, count: 0 } };
const GasRecebimentosView = ({ $state, GasService, UIService }) => {
    const [from, setFrom] = useState(() => ($state.params.from ? moment($state.params.from) : moment().startOf('month')));
    const [to, setTo] = useState(() => ($state.params.to ? moment($state.params.to) : moment()));
    const [loading, setLoading] = useState(false);
    const [raw, setRaw] = useState(EMPTY);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
    const [sort, setSort] = useState([]);
    const [filter, setFilter] = useState([]);
    const fetchData = async () => {
        if (!from || !to || !from.isValid() || !to.isValid())
            return;
        setLoading(true);
        try {
            const result = await GasService.getRecebimentosPorTecnico(from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'));
            setRaw(result ?? EMPTY);
            $state.go($state.current.name, { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }, { notify: false, reload: false, location: 'replace', inherit: true });
        }
        catch (e) {
            console.log(e);
            UIService.addToast('Ocorreu um erro ao carregar os recebimentos.');
        }
        setLoading(false);
    };
    useEffect(() => {
        setPagination(p => ({ ...p, pageIndex: 0 }));
        fetchData();
    }, [from, to]);
    // Colunas dinâmicas: Técnico + uma por forma de pagamento + Total + Nº
    const columns = useMemo(() => {
        const moneyCell = ({ cell }) => <span>{money(cell.getValue() ?? 0)}</span>;
        const alignRight = { muiTableHeadCellProps: { align: 'right' }, muiTableBodyCellProps: { align: 'right' } };
        return [
            { accessorKey: 'tecnicoNome', header: 'Técnico', size: 200 },
            ...raw.formas.map(f => ({
                accessorKey: `forma_${f.id}`,
                header: f.designacao,
                Cell: moneyCell,
                ...alignRight
            })),
            { accessorKey: 'total', header: 'Total', Cell: moneyCell, ...alignRight },
            { accessorKey: 'count', header: 'Nº Recebimentos', size: 70, ...alignRight }
        ];
    }, [raw.formas]);
    // Opções de filtro/ordenação para o componente Table (mesmas capacidades da Carteira)
    const options = useMemo(() => ({
        filter: [
            { name: 'tecnicoNome', type: PaginationFilterType.TEXT },
            ...raw.formas.map(f => ({ name: `forma_${f.id}`, type: PaginationFilterType.NUMBER })),
            { name: 'total', type: PaginationFilterType.NUMBER },
            { name: 'count', type: PaginationFilterType.NUMBER }
        ],
        sort: ['tecnicoNome', ...raw.formas.map(f => `forma_${f.id}`), 'total', 'count']
    }), [raw.formas]);
    // Achata a matriz em linhas e aplica filtro + ordenação + paginação em memória
    const data = useMemo(() => {
        let rows = raw.tecnicos.map(t => {
            const row = { tecnicoId: t.tecnicoId, tecnicoNome: t.tecnicoNome, total: t.total, count: t.count };
            raw.formas.forEach(f => {
                row[`forma_${f.id}`] = t.porForma[f.id] ?? 0;
            });
            return row;
        });
        // Filtro
        filter.forEach(f => {
            const val = String(f.value ?? '').toLowerCase().trim();
            if (!val)
                return;
            rows = rows.filter(r => {
                const cell = r[f.id];
                if (cell == null)
                    return false;
                return String(cell).toLowerCase().includes(val);
            });
        });
        // Ordenação (primeira coluna ordenada)
        if (sort.length) {
            const s = sort[0];
            rows = [...rows].sort((a, b) => {
                const av = a[s.id];
                const bv = b[s.id];
                let c;
                if (typeof av === 'number' && typeof bv === 'number')
                    c = av - bv;
                else
                    c = String(av ?? '').localeCompare(String(bv ?? ''));
                return s.desc ? -c : c;
            });
        }
        const totalItems = rows.length;
        const pageSize = pagination.pageSize || 20;
        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
        const start = pagination.pageIndex * pageSize;
        const pageRows = rows.slice(start, start + pageSize);
        return {
            data: pageRows,
            meta: { currentPage: pagination.pageIndex + 1, itemsPerPage: pageSize, totalItems, totalPages },
            links: {}
        };
    }, [raw, filter, sort, pagination]);
    const exportXlsx = () => {
        if (!raw.tecnicos.length) {
            UIService.addToast('Sem dados para exportar.');
            return;
        }
        const header = ['Técnico', ...raw.formas.map(f => f.designacao), 'Total', 'Nº Recebimentos'];
        const body = raw.tecnicos.map(t => [t.tecnicoNome, ...raw.formas.map(f => t.porForma[f.id] ?? 0), t.total, t.count]);
        const totalRow = ['TOTAL', ...raw.formas.map(f => raw.totais.porForma[f.id] ?? 0), raw.totais.total, raw.totais.count];
        const ws = XLSX.utils.aoa_to_sheet([header, ...body, totalRow]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Recebimentos');
        XLSX.writeFile(wb, `gas-recebimentos-${from.format('YYYY-MM-DD')}-${to.format('YYYY-MM-DD')}.xlsx`);
    };
    // Totais por forma de pagamento (para o cartão de resumo)
    const resumoContent = useMemo(() => {
        const base = [
            { label: 'Total recebido:', value: money(raw.totais.total) },
            { label: 'Nº de recebimentos:', value: String(raw.totais.count) },
            { label: 'Nº de técnicos:', value: String(raw.tecnicos.length) }
        ];
        const porForma = raw.formas.map(f => ({
            label: `${f.designacao}:`,
            value: money(raw.totais.porForma[f.id] ?? 0)
        }));
        return [...base, ...porForma];
    }, [raw]);
    return (<Box>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Breadcrumbs aria-label='breadcrumb'>
          <Link underline='none' color='inherit'>
            Gás
          </Link>
          <Link underline='hover' color='text.primary'>
            Recebimentos por Técnico
          </Link>
        </Breadcrumbs>
      </Box>

      <Grid container spacing={1}>
        <Grid xs={12} md={6}>
          <DynamicCard title='Filtros' sx={{ height: '100%' }} content={[
            {
                label: 'De:',
                value: (<LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker format='DD/MM/YYYY' value={from} onChange={value => value && setFrom(value)} slotProps={{ textField: { variant: 'outlined', fullWidth: true, size: 'small' } }}/>
                  </LocalizationProvider>)
            },
            {
                label: 'Até:',
                value: (<LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker format='DD/MM/YYYY' value={to} onChange={value => value && setTo(value)} slotProps={{ textField: { variant: 'outlined', fullWidth: true, size: 'small' } }}/>
                  </LocalizationProvider>)
            }
        ]} loading={false}/>
        </Grid>
        <Grid xs={12} md={6}>
          <DynamicCard title='Resumo' sx={{ height: '100%' }} content={resumoContent} loading={false}/>
        </Grid>

        <Grid xs={12} md={12}>
          <Table storageKey={$state.current.name} data={data} columns={columns} initial={{ sort, filter }} isLoading={loading} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} onExport={exportXlsx}/>
        </Grid>
      </Grid>
    </Box>);
};
export default GasRecebimentosView;
